// Importaciones y clase TeamStoreService
import { computed, Injectable, Signal, signal } from "@angular/core";
import { Observable, of } from "rxjs";

import { AppConstants } from "@mp/app.constants";
import { TeamEntity, TeamEntityHelper } from "@mp/common/entities/team.entity";
import { IListDataResponse } from "@m481/mp-sdk-ng/lib/models/reponses/list-data.response";
import { TeamModel } from "@m481/mp-sdk-ng/lib/models/team.model";
import { BaseStoreService } from "./base-store.service";

@Injectable({
    providedIn: "root"
})
export class TeamStoreService extends BaseStoreService<TeamEntity, TeamModel> {
    public readonly selectedTeamId = signal<string | null>(null);

    constructor() {
        super({
            storageKey: AppConstants.StorageKeys.STORE.TEAM
        });

        this.hydrateSelectedTeamId();
        this.init();
    }

    public override reset(): void {
        super.reset();
        this.removeSelectedTeam();
    }

    /*
     * Selectors
     */

    public selectUserTeams(userId: string): Signal<Array<TeamEntity>> {
        return computed(() => {
            const teams = this.entities();
            const hasMembershipData = teams.some(t => (t.usersIds?.length ?? 0) > 0 || (t.managersIds?.length ?? 0) > 0);
            return hasMembershipData ? teams.filter(team => team.usersIds.includes(userId) || team.managersIds.includes(userId)) : teams;
        });
    }

    public selectUserSelectedTeam(userId: string): Signal<TeamEntity | null> {
        return computed(() => {
            const selectedTeamId = this.selectedTeamId();
            if (!selectedTeamId) {
                return null;
            }
            return this.selectUserTeams(userId)().find(team => team.id === selectedTeamId) || null;
        });
    }

    /*
     * Methods to manage user preferences
     */

    public hydrateSelectedTeamId(): void {
        const persistedTeamId = this.storageService.getItem(AppConstants.StorageKeys.USER.TEAM);
        this.selectedTeamId.set(persistedTeamId ?? null);
    }

    public saveSelectedTeam(teamId: string): void {
        this.storageService.setItem(AppConstants.StorageKeys.USER.TEAM, teamId);
        this.selectedTeamId.set(teamId);
    }

    public removeSelectedTeam(): void {
        this.storageService.removeItem(AppConstants.StorageKeys.USER.TEAM);
        this.selectedTeamId.set(null);
    }

    /*
     * Base store service SDK methods implementation
     */

    protected mapEntityToModel(entity: TeamEntity): TeamModel {
        return TeamEntityHelper.toModel(entity);
    }

    protected mapModelToEntity(model: TeamModel): TeamEntity {
        return TeamEntityHelper.fromModel(model);
    }

    protected sdkGet$(): Observable<IListDataResponse<TeamModel>> {
        return this.mpSdkService.getTeams();
    }

    protected sdkCreate$(model: TeamModel): Observable<TeamModel> {
        //Not yet implemented in sdk
        return of(model);
    }

    protected sdkUpdate$(model: TeamModel): Observable<TeamModel> {
        //Not yet implemented in sdk
        return of(model);
    }

    protected sdkDelete$(id: string): Observable<boolean> {
        //Not yet implemented in sdk
        return of(true);
    }
}
